import {Component, OnInit} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {DataService} from '../../../Services/data.service';
import {ChatService} from '../../../Services/chat.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {DatePipe} from '@angular/common';
import {firstValueFrom} from 'rxjs';

interface BatchItem {
  id: string;
  header_date: string;
  number: string;
  description: string;
  detail_total: number;
  item_type_id: string;
  item_type_name: string;
}

interface RecurringBatch {
  id: string;
  description: string;
  batch_type_id: number;
  batch_type_name: string;
  user_id: string;
  user_name: string;
  items: BatchItem[];
  selected: boolean;
  expanded: boolean;
}

interface OpenBatch {
  id: string;
  description: string;
  batch_type_id: number;
  batch_type_name: string;
  user_id: string;
  user_name: string;
  items: BatchItem[];
  selected: boolean;
  expanded: boolean;
}

const BATCH_TYPE_LABELS: Record<number, string> = {
  1: 'GL Journal Entries',
  2: 'AR Invoices',
  3: 'AR Payments',
  4: 'AP Invoices',
  5: 'AP Payments',
  8: 'Quotes',
  9: 'Sales Orders',
  10: 'Purchase Orders',
  11: 'AR Shipments',
  12: 'AP Shipments',
  14: 'Assembly',
  15: 'Transfer',
  16: 'Statistical',
};

@Component({
  standalone: false,
  selector: 'app-admin-operations',
  templateUrl: './admin-operations.component.html',
})
export class AdminOperationsComponent implements OnInit {
  vm: any = {
    // batch posting
    openBatches: [] as OpenBatch[],
    loading: false,
    posting: false,
    periodEndDate: '',
    allSelected: false,
    notifyUsers: true,
    // recurring batches list
    recurringBatches: [] as RecurringBatch[],
    recurringLoading: false,
    recurringPosting: false,
    recurringAllSelected: false,
    recurringNotifyUsers: true,
    recurringPeriodEndDate: '',
    // recurring processing
    recurringRunning: false,
    recurringLastRun: null as string | null,
    // fixed assets
    depreciationRunning: false,
    depreciationDate: '',
    depreciationLastRun: null as string | null,
    // data integrity
    rebuildRunning: false,
    purgeRunning: false,
    purgeDays: 90,
    // integrations
    stripeSyncRunning: false,
    posSyncRunning: false,
  };

  private currentUserId = '';
  private currentUserName = '';

  constructor(
    private titleService: Title,
    private tableDataService: DataService,
    private chatService: ChatService,
    private notification: BsToastService,
    private confirmDialog: ConfirmDialogService,
    private datePipe: DatePipe,
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle($localize`:Admin Operations@@adminOperations:Admin Operations`);
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    this.currentUserId = (currentUser?.id ?? currentUser?.userId ?? '').toString();
    this.currentUserName = currentUser?.name || currentUser?.username || 'Administrator';
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd') ?? '';
    this.vm.periodEndDate = today;
    this.vm.recurringPeriodEndDate = today;
    this.vm.depreciationDate = today;
    this.loadOpenBatches();
    // this.loadRecurringBatches();
  }

  // ─── Batch Posting ────────────────────────────────────────────────────────

  loadOpenBatches(): void {
    this.vm.loading = true;
    this.vm.openBatches = [];
    const toDate = this.vm.periodEndDate || this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    this.tableDataService.getData('t_batch', `open-to-date?toDate=${toDate}`).subscribe({
      next: (result: any) => {
        console.log('open batches', result);
        const batches: any[] = Array.isArray(result) ? result : [];
        this.vm.openBatches = batches.map((b: any) => ({...b, selected: false, expanded: false}));
        this.vm.loading = false;
      },
      error: () => {
        this.vm.loading = false;
        this.notification.showErrorToast($localize`:Error loading batches@@errorLoadingBatches:Error loading open batches`);
      },
    });
  }

  loadRecurringBatches(): void {
    this.vm.recurringLoading = true;
    this.vm.recurringBatches = [];
    const toDate = this.vm.recurringPeriodEndDate || this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    this.tableDataService.getData('t_batch', `open-to-date?toDate=${toDate}&recurring=true`).subscribe({
      next: (result: any) => {
        console.log('recurring batches', result);
        const batches: any[] = Array.isArray(result) ? result : [];
        this.vm.recurringBatches = batches.map((b: any) => ({...b, selected: false, expanded: false}));
        this.vm.recurringAllSelected = false;
        this.vm.recurringLoading = false;
      },
      error: () => {
        this.vm.recurringLoading = false;
        this.notification.showErrorToast($localize`:Error loading recurring batches@@errorLoadingRecurringBatches:Error loading recurring batches`);
      },
    });
  }

  batchTypeLabel(typeId: number): string {
    return BATCH_TYPE_LABELS[typeId] ?? `Type ${typeId}`;
  }

  toggleSelectAll(): void {
    this.vm.allSelected = !this.vm.allSelected;
    this.vm.openBatches.forEach((b: OpenBatch) => (b.selected = this.vm.allSelected));
  }

  get selectedBatches(): OpenBatch[] {
    return this.vm.openBatches.filter((b: OpenBatch) => b.selected);
  }

  get selectedCount(): number {
    return this.selectedBatches.length;
  }

  toggleSelectAllRecurring(): void {
    this.vm.recurringAllSelected = !this.vm.recurringAllSelected;
    this.vm.recurringBatches.forEach((b: RecurringBatch) => (b.selected = this.vm.recurringAllSelected));
  }

  get selectedRecurringBatches(): RecurringBatch[] {
    return this.vm.recurringBatches.filter((b: RecurringBatch) => b.selected);
  }

  get selectedRecurringCount(): number {
    return this.selectedRecurringBatches.length;
  }

  postSelectedRecurringBatches(): void {
    if (!this.selectedRecurringCount) {
      this.notification.showWarningToast($localize`:No recurring batches selected@@noRecurringBatchesSelected:Please select at least one recurring batch to post`);
      return;
    }
    const dateLabel = this.datePipe.transform(this.vm.recurringPeriodEndDate, 'mediumDate') ?? this.vm.recurringPeriodEndDate;
    const message = $localize`:Confirm post recurring batches@@confirmPostRecurringBatches:Post ${this.selectedRecurringCount} recurring batch(es) through ${dateLabel}? This action cannot be undone.`;
    this.confirmDialog.confirmThis(
      [message],
      () => this.executeRecurringPost(dateLabel),
      () => {
      }
    );
  }

  private async executeRecurringPost(dateLabel: string): Promise<void> {
    this.vm.recurringPosting = true;
    let successCount = 0;
    let errorCount = 0;

    for (const batch of this.selectedRecurringBatches) {
      try {
        await this.tableDataService.patch('t_batch', batch.id, {locked: 'true'});
        successCount++;
        if (this.vm.recurringNotifyUsers && batch.user_id && batch.user_id !== this.currentUserId) {
          await this.sendRecurringBatchNotification(batch, dateLabel);
        }
      } catch {
        errorCount++;
        this.notification.showErrorToast(
          $localize`:Error posting recurring batch@@errorPostingRecurringBatch:Error posting recurring batch: ${batch.description}`
        );
      }
    }

    this.vm.recurringPosting = false;
    if (successCount > 0) {
      this.notification.showSuccessToast(
        $localize`:Recurring batches posted@@recurringBatchesPosted:${successCount} recurring batch(es) posted successfully` +
        (errorCount > 0 ? ` (${errorCount} failed)` : '')
      );
    }
    this.loadRecurringBatches();
  }

  private async sendRecurringBatchNotification(batch: RecurringBatch, dateLabel: string): Promise<void> {
    try {
      const thread = await firstValueFrom(
        this.chatService.createOrGetThread(this.currentUserId, batch.user_id)
      );
      const message = $localize`:Recurring batch auto-post notification@@recurringBatchAutoPostNotification:Your recurring batch "${batch.description}" (${this.batchTypeLabel(batch.batch_type_id)}) has been automatically posted through ${dateLabel} by ${this.currentUserName} as part of period-end processing.`;
      await firstValueFrom(
        this.chatService.sendMessage(thread.id, this.currentUserId, message)
      );
    } catch {
      // Notification failure is non-fatal
    }
  }

  postSelectedBatches(): void {
    if (!this.selectedCount) {
      this.notification.showWarningToast($localize`:No batches selected@@noBatchesSelected:Please select at least one batch to post`);
      return;
    }
    const dateLabel = this.datePipe.transform(this.vm.periodEndDate, 'mediumDate') ?? this.vm.periodEndDate;
    const message = $localize`:Confirm post batches@@confirmPostBatches:Post ${this.selectedCount} open batch(es) through ${dateLabel}? This action cannot be undone.`;
    this.confirmDialog.confirmThis(
      [message],
      () => this.executePost(dateLabel),
      () => {
      }
    );
  }

  private async executePost(dateLabel: string): Promise<void> {
    this.vm.posting = true;
    let successCount = 0;
    let errorCount = 0;

    for (const batch of this.selectedBatches) {
      try {
        await this.tableDataService.patch('t_batch', batch.id, {locked: 'true'});
        successCount++;
        if (this.vm.notifyUsers && batch.user_id && batch.user_id !== this.currentUserId) {
          await this.sendBatchNotification(batch, dateLabel);
        }
      } catch {
        errorCount++;
        this.notification.showErrorToast(
          $localize`:Error posting batch@@errorPostingBatch:Error posting batch: ${batch.description}`
        );
      }
    }

    this.vm.posting = false;
    if (successCount > 0) {
      this.notification.showSuccessToast(
        $localize`:Batches posted@@batchesPosted:${successCount} batch(es) posted successfully` +
        (errorCount > 0 ? ` (${errorCount} failed)` : '')
      );
    }
    this.loadOpenBatches();
  }

  private async sendBatchNotification(batch: OpenBatch, dateLabel: string): Promise<void> {
    try {
      const thread = await firstValueFrom(
        this.chatService.createOrGetThread(this.currentUserId, batch.user_id)
      );
      const message = $localize`:Batch auto-post notification@@batchAutoPostNotification:Your batch "${batch.description}" (${this.batchTypeLabel(batch.batch_type_id)}) has been automatically posted through ${dateLabel} by ${this.currentUserName} as part of period-end processing.`;
      await firstValueFrom(
        this.chatService.sendMessage(thread.id, this.currentUserId, message)
      );
    } catch {
      // Notification failure is non-fatal — batch was already posted
    }
  }

  // ─── Delete empty batch ───────────────────────────────────────────────────

  deleteBatch(batch: OpenBatch): void {
    this.confirmDialog.confirmThis(
      [$localize`:Confirm delete empty batch@@confirmDeleteEmptyBatch:Delete empty batch "${batch.description}"? This cannot be undone.`],
      () => {
        this.tableDataService.deleteDataById('t_batch', batch.id).subscribe({
          next: async () => {
            this.notification.showSuccessToast(
              $localize`:Batch deleted@@batchDeleted:Batch "${batch.description}" deleted successfully`
            );
            if (batch.user_id && batch.user_id !== this.currentUserId) {
              await this.sendDeleteBatchNotification(batch);
            }
            this.loadOpenBatches();
          },
          error: () => {
            this.notification.showErrorToast(
              $localize`:Error deleting batch@@errorDeletingBatch:Error deleting batch: ${batch.description}`
            );
          },
        });
      },
      () => {
      }
    );
  }

  private async sendDeleteBatchNotification(batch: OpenBatch): Promise<void> {
    try {
      const thread = await firstValueFrom(
        this.chatService.createOrGetThread(this.currentUserId, batch.user_id)
      );
      const message = $localize`:Batch deleted notification@@batchDeletedNotification:Your empty batch "${batch.description}" (${this.batchTypeLabel(batch.batch_type_id)}) has been deleted by ${this.currentUserName} during admin operations.`;
      await firstValueFrom(
        this.chatService.sendMessage(thread.id, this.currentUserId, message)
      );
    } catch {
      // Notification failure is non-fatal — batch was already deleted
    }
  }

  // ─── Recurring Processing ─────────────────────────────────────────────────

  postDueRecurringJournals(): void {
    this.confirmDialog.confirmThis(
      [$localize`:Confirm post recurring journals@@confirmPostRecurringJournals:Post all recurring journal entries due as of today? This will create new journal entry batches.`],
      () => {
        this.vm.recurringRunning = true;
        this.tableDataService.post('EntryRecurring/post-due', {asOfDate: this.vm.periodEndDate}).subscribe({
          next: (result: any) => {
            this.vm.recurringRunning = false;
            this.vm.recurringLastRun = this.datePipe.transform(new Date(), 'medium');
            this.notification.showSuccessToast(
              $localize`:Recurring journals posted@@recurringJournalsPosted:Recurring journals posted: ${result?.count ?? 0} entries created`
            );
          },
          error: () => {
            this.vm.recurringRunning = false;
            this.notification.showErrorToast($localize`:Error posting recurring journals@@errorPostingRecurringJournals:Error posting recurring journal entries`);
          },
        });
      },
      () => {
      }
    );
  }

  postDueRecurringInvoices(): void {
    this.confirmDialog.confirmThis(
      [$localize`:Confirm post recurring invoices@@confirmPostRecurringInvoices:Post all recurring invoices due as of today? This will create new invoice batches.`],
      () => {
        this.vm.recurringRunning = true;
        this.tableDataService.post('InvoiceRecurring/post-due', {asOfDate: this.vm.periodEndDate}).subscribe({
          next: (result: any) => {
            this.vm.recurringRunning = false;
            this.vm.recurringLastRun = this.datePipe.transform(new Date(), 'medium');
            this.notification.showSuccessToast(
              $localize`:Recurring invoices posted@@recurringInvoicesPosted:Recurring invoices posted: ${result?.count ?? 0} invoices created`
            );
          },
          error: () => {
            this.vm.recurringRunning = false;
            this.notification.showErrorToast($localize`:Error posting recurring invoices@@errorPostingRecurringInvoices:Error posting recurring invoices`);
          },
        });
      },
      () => {
      }
    );
  }

  // ─── Fixed Assets ─────────────────────────────────────────────────────────

  runDepreciation(): void {
    if (!this.vm.depreciationDate) {
      this.notification.showWarningToast($localize`:Depreciation date required@@depreciationDateRequired:Please select a through-date for depreciation`);
      return;
    }
    const dateLabel = this.datePipe.transform(this.vm.depreciationDate, 'mediumDate') ?? this.vm.depreciationDate;
    this.confirmDialog.confirmThis(
      [$localize`:Confirm run depreciation@@confirmRunDepreciation:Run depreciation for all fixed assets through ${dateLabel}? This action cannot be undone.`],
      () => {
        this.vm.depreciationRunning = true;
        this.tableDataService.post('FixedAssets/run-depreciation', {throughDate: this.vm.depreciationDate}).subscribe({
          next: (result: any) => {
            this.vm.depreciationRunning = false;
            this.vm.depreciationLastRun = this.datePipe.transform(new Date(), 'medium');
            this.notification.showSuccessToast(
              $localize`:Depreciation run complete@@depreciationRunComplete:Depreciation run complete: ${result?.count ?? 0} assets processed`
            );
          },
          error: () => {
            this.vm.depreciationRunning = false;
            this.notification.showErrorToast($localize`:Error running depreciation@@errorRunningDepreciation:Error running depreciation`);
          },
        });
      },
      () => {
      }
    );
  }

  // ─── Data Integrity ───────────────────────────────────────────────────────

  rebuildAccountBalances(): void {
    this.confirmDialog.confirmThis(
      [$localize`:Confirm rebuild balances@@confirmRebuildBalances:Rebuild all account running balances from transaction history? This may take several minutes.`],
      () => {
        this.vm.rebuildRunning = true;
        this.tableDataService.post('Accounts/rebuild-balances', {}).subscribe({
          next: () => {
            this.vm.rebuildRunning = false;
            this.notification.showSuccessToast($localize`:Balances rebuilt@@balancesRebuilt:Account balances rebuilt successfully`);
          },
          error: () => {
            this.vm.rebuildRunning = false;
            this.notification.showErrorToast($localize`:Error rebuilding balances@@errorRebuildingBalances:Error rebuilding account balances`);
          },
        });
      },
      () => {
      }
    );
  }

  purgeAuditTrail(): void {
    if (!this.vm.purgeDays || this.vm.purgeDays < 1) {
      this.notification.showWarningToast($localize`:Purge days required@@purgeDaysRequired:Please enter a valid number of days`);
      return;
    }
    this.confirmDialog.confirmThis(
      [$localize`:Confirm purge audit trail@@confirmPurgeAuditTrail:Permanently delete all audit trail records older than ${this.vm.purgeDays} days? This cannot be undone.`],
      () => {
        this.vm.purgeRunning = true;
        this.tableDataService.post('AuditTrail/purge', {olderThanDays: this.vm.purgeDays}).subscribe({
          next: (result: any) => {
            this.vm.purgeRunning = false;
            this.notification.showSuccessToast(
              $localize`:Audit trail purged@@auditTrailPurged:Audit trail purged: ${result?.count ?? 0} records deleted`
            );
          },
          error: () => {
            this.vm.purgeRunning = false;
            this.notification.showErrorToast($localize`:Error purging audit trail@@errorPurgingAuditTrail:Error purging audit trail`);
          },
        });
      },
      () => {
      }
    );
  }

  // ─── Integrations ─────────────────────────────────────────────────────────

  resyncStripe(): void {
    this.confirmDialog.confirmThis(
      [$localize`:Confirm resync Stripe@@confirmResyncStripe:Re-sync all Stripe data? This may take a few minutes.`],
      () => {
        this.vm.stripeSyncRunning = true;
        this.tableDataService.post('Stripe/resync', {}).subscribe({
          next: () => {
            this.vm.stripeSyncRunning = false;
            this.notification.showSuccessToast($localize`:Stripe synced@@stripeSynced:Stripe data re-synced successfully`);
          },
          error: () => {
            this.vm.stripeSyncRunning = false;
            this.notification.showErrorToast($localize`:Error syncing Stripe@@errorSyncingStripe:Error re-syncing Stripe data`);
          },
        });
      },
      () => {
      }
    );
  }

  resyncPos(): void {
    this.confirmDialog.confirmThis(
      [$localize`:Confirm resync POS@@confirmResyncPos:Re-sync all POS data? This may take a few minutes.`],
      () => {
        this.vm.posSyncRunning = true;
        this.tableDataService.post('Pos/resync', {}).subscribe({
          next: () => {
            this.vm.posSyncRunning = false;
            this.notification.showSuccessToast($localize`:POS synced@@posSynced:POS data re-synced successfully`);
          },
          error: () => {
            this.vm.posSyncRunning = false;
            this.notification.showErrorToast($localize`:Error syncing POS@@errorSyncingPos:Error re-syncing POS data`);
          },
        });
      },
      () => {
      }
    );
  }
}
